/**
 * EcosystemForm: the create / edit form for a tenant ecosystem.
 *
 * P7 wave 3a. Used by `EcosystemManager` for both creating a new tenant
 * ecosystem and editing an existing one; `mode` ('create' | 'edit') selects
 * the behaviour difference. The form is rendered inside a Bootstrap modal by
 * the caller.
 *
 * Fields:
 *   - slug: kebab-case identifier. CREATE-ONLY: it is the immutable
 *                    universal identifier, so in edit mode it is shown
 *                    read-only. Server-side schema validators reject a bad /
 *                    reserved / duplicate slug; those 400s surface inline.
 *   - name: required display name.
 *   - shortName: optional short name for tight UI.
 *   - description: optional 1-2 paragraph description.
 *   - branding: logo URL, primary / secondary hex colours, favicon URL.
 *   - capabilities: the 8 per-ecosystem feature flags, as checkboxes.
 *   - taxonomy: regions / sectors / programmingTypes, each authored as a
 *                    textarea of newline-or-comma-separated values. Kept
 *                    deliberately simple per the wave brief.
 *   - status: active | archived | pending. Never `platform` (the
 *                    platform pseudo-Ecosystem is not editable here, and the
 *                    server rejects a `platform` status anyway).
 *
 * The form does not call the API itself: it lifts the assembled payload to
 * the caller's `onSubmit`, which owns the create / update request and the
 * surfacing of server errors. `serverError` (a string) is passed back down so
 * a slug-validation 400 renders inline next to the form.
 *
 * No inline CSS: Bootstrap utilities / PlatformAdmin.css only.
 */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Row, Col, Button, Alert, Spinner } from 'react-bootstrap';

import './PlatformAdmin.css';

// The 8 per-ecosystem capability flags, paired with human-readable labels.
// Order and keys mirror the Ecosystem schema's `capabilities` sub-document.
const CAPABILITY_FIELDS = [
    { key: 'mentoring', label: 'Mentoring' },
    { key: 'opportunities', label: 'Opportunities' },
    { key: 'opportunityPartnerships', label: 'Opportunity Partnerships' },
    { key: 'networkGraph', label: 'Network Visualization' },
    { key: 'affiliations', label: 'Affiliations' },
    { key: 'discoverPartners', label: 'Discover Partners' },
    { key: 'ecosystemAnalytics', label: 'Ecosystem Analytics' },
    { key: 'aiAssistant', label: 'AI Assistant' },
];

// The taxonomy axes, each authored as a free-text list.
const TAXONOMY_FIELDS = [
    {
        key: 'regions',
        label: 'Regions',
        help: 'Geographic regions this ecosystem serves.',
    },
    {
        key: 'sectors',
        label: 'Sectors',
        help: 'Organization sector / type categories.',
    },
    {
        key: 'programmingTypes',
        label: 'Programming types',
        help: 'STEM programming categories.',
    },
];

// Tenant-assignable lifecycle states. `platform` is intentionally absent.
// `archived` is also intentionally absent: v1.0.1 #60: selecting "Archived"
// in this generic edit form silently archived an ecosystem without the
// dedicated Archive button's confirmation modal. Archiving is irreversible
// from the user's perspective (data is preserved but visibility flips), so
// it must go through the dedicated path with its own confirmation flow.
const STATUS_OPTIONS = [
    { value: 'pending', label: 'Pending: created, not yet activated' },
    { value: 'active', label: 'Active: live and listed publicly' },
];

// Capabilities all default to enabled, matching the schema defaults.
const DEFAULT_CAPABILITIES = CAPABILITY_FIELDS.reduce((acc, { key }) => {
    acc[key] = true;
    return acc;
}, {});

/**
 * Split a free-text list (newline- or comma-separated) into a trimmed,
 * de-duplicated, empty-free array. Used to turn a taxonomy textarea into the
 * array the API expects.
 *
 * @param {string} text - The textarea value.
 * @returns {string[]} The parsed list.
 */
const parseList = (text) => {
    if (!text) {
        return [];
    }
    const seen = new Set();
    return text
        .split(/[\n,]+/)
        .map((item) => item.trim())
        .filter((item) => {
            if (!item || seen.has(item)) {
                return false;
            }
            seen.add(item);
            return true;
        });
};

/**
 * Join an array into a newline-separated textarea value for editing.
 *
 * @param {string[]} list - The taxonomy array.
 * @returns {string} The textarea value.
 */
const joinList = (list) => (Array.isArray(list) ? list.join('\n') : '');

/**
 * Build the form's initial state from an existing ecosystem (edit mode) or
 * from blank defaults (create mode).
 *
 * @param {object|null} ecosystem - The ecosystem being edited, or null.
 * @returns {object} The initial form state.
 */
const buildInitialState = (ecosystem) => {
    const eco = ecosystem || {};
    const branding = eco.branding || {};
    const capabilities = eco.capabilities || {};
    const taxonomy = eco.taxonomy || {};
    return {
        slug: eco.slug || '',
        name: eco.name || '',
        shortName: eco.shortName || '',
        description: eco.description || '',
        logoUrl: branding.logoUrl || '',
        primaryColor: branding.primaryColor || '',
        secondaryColor: branding.secondaryColor || '',
        faviconUrl: branding.faviconUrl || '',
        // Capability flags: an existing ecosystem's stored value wins; an
        // unset key falls back to the enabled default.
        capabilities: CAPABILITY_FIELDS.reduce((acc, { key }) => {
            acc[key] =
                capabilities[key] !== undefined
                    ? Boolean(capabilities[key])
                    : DEFAULT_CAPABILITIES[key];
            return acc;
        }, {}),
        regions: joinList(taxonomy.regions),
        sectors: joinList(taxonomy.sectors),
        programmingTypes: joinList(taxonomy.programmingTypes),
        status: eco.status || 'pending',
    };
};

export default function EcosystemForm({
    mode,
    ecosystem,
    submitting,
    serverError,
    onSubmit,
    onCancel,
}) {
    const isEdit = mode === 'edit';

    // Initial state is computed once from the passed ecosystem. The caller
    // remounts the form (via a React key) when switching the edited record,
    // so a stale initial state is not a concern.
    const [form, setForm] = useState(() => buildInitialState(ecosystem));
    // Client-side validation messages, keyed by field name.
    const [errors, setErrors] = useState({});

    const hexPattern = useMemo(() => /^#[0-9a-fA-F]{6}$/, []);
    const slugPattern = useMemo(() => /^[a-z][a-z0-9-]*[a-z0-9]$/, []);

    const setField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const setCapability = (key, checked) => {
        setForm((prev) => ({
            ...prev,
            capabilities: { ...prev.capabilities, [key]: checked },
        }));
    };

    /**
     * Validate the form. Returns a map of field -> message; an empty map
     * means the form is valid. Mirrors the server-side schema rules so the
     * obvious mistakes are caught before a round-trip: the server remains
     * the authority (it also catches reserved / duplicate slugs).
     */
    const validate = () => {
        const next = {};
        if (!isEdit) {
            const slug = form.slug.trim();
            if (!slug) {
                next.slug = 'A slug is required.';
            } else if (!slugPattern.test(slug)) {
                next.slug =
                    'Use kebab-case: lowercase letters, digits and hyphens; ' +
                    'must start with a letter and end with a letter or digit.';
            }
        }
        if (!form.name.trim()) {
            next.name = 'A name is required.';
        }
        if (form.primaryColor && !hexPattern.test(form.primaryColor.trim())) {
            next.primaryColor = 'Enter a hex colour like #C8102E.';
        }
        if (
            form.secondaryColor &&
            !hexPattern.test(form.secondaryColor.trim())
        ) {
            next.secondaryColor = 'Enter a hex colour like #C8102E.';
        }
        return next;
    };

    /**
     * Assemble the API payload from the current form state. Branding,
     * capabilities and taxonomy are always sent as whole sub-objects so an
     * edit cannot accidentally drop a field. The slug is included only on
     * create (it is immutable on edit, and the server ignores it there).
     */
    const buildPayload = () => {
        const payload = {
            name: form.name.trim(),
            shortName: form.shortName.trim(),
            description: form.description.trim(),
            status: form.status,
            branding: {
                logoUrl: form.logoUrl.trim(),
                primaryColor: form.primaryColor.trim(),
                secondaryColor: form.secondaryColor.trim(),
                faviconUrl: form.faviconUrl.trim(),
            },
            capabilities: { ...form.capabilities },
            taxonomy: {
                regions: parseList(form.regions),
                sectors: parseList(form.sectors),
                programmingTypes: parseList(form.programmingTypes),
            },
        };
        if (!isEdit) {
            payload.slug = form.slug.trim().toLowerCase();
        }
        return payload;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const validation = validate();
        setErrors(validation);
        if (Object.keys(validation).length > 0) {
            return;
        }
        onSubmit(buildPayload());
    };

    return (
        <Form noValidate onSubmit={handleSubmit}>
            {serverError && (
                <Alert variant="danger" className="mb-3">
                    {serverError}
                </Alert>
            )}

            {/* --- Identity --------------------------------------------- */}
            <Form.Group className="mb-3" controlId="ecosystem-slug">
                <Form.Label>Slug</Form.Label>
                <Form.Control
                    type="text"
                    value={form.slug}
                    placeholder="e.g. costem"
                    readOnly={isEdit}
                    plaintext={isEdit}
                    isInvalid={Boolean(errors.slug)}
                    onChange={(e) =>
                        setField('slug', e.target.value.toLowerCase())
                    }
                />
                <Form.Control.Feedback type="invalid">
                    {errors.slug}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                    {isEdit
                        ? 'The slug is the permanent identifier and cannot be changed.'
                        : 'Kebab-case identifier used in URLs. Cannot be a reserved name.'}
                </Form.Text>
            </Form.Group>

            <Row>
                <Col md={7}>
                    <Form.Group className="mb-3" controlId="ecosystem-name">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={form.name}
                            isInvalid={Boolean(errors.name)}
                            onChange={(e) => setField('name', e.target.value)}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.name}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={5}>
                    <Form.Group
                        className="mb-3"
                        controlId="ecosystem-shortName"
                    >
                        <Form.Label>Short name</Form.Label>
                        <Form.Control
                            type="text"
                            value={form.shortName}
                            placeholder="e.g. COSTEM"
                            onChange={(e) =>
                                setField('shortName', e.target.value)
                            }
                        />
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3" controlId="ecosystem-description">
                <Form.Label>Description</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    value={form.description}
                    onChange={(e) => setField('description', e.target.value)}
                />
            </Form.Group>

            {/* --- Status ----------------------------------------------- */}
            {/* If the ecosystem is currently archived, show a read-only
                indicator instead of the editable dropdown. Restoring an
                archived ecosystem goes through the dedicated Restore path,
                mirroring the Archive button on the active side. */}
            <Form.Group className="mb-3" controlId="ecosystem-status">
                <Form.Label>Status</Form.Label>
                {form.status === 'archived' ? (
                    <Form.Control
                        type="text"
                        value="Archived (restore via the Restore action)"
                        readOnly
                        plaintext
                    />
                ) : (
                    <Form.Select
                        value={form.status}
                        onChange={(e) => setField('status', e.target.value)}
                    >
                        {STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Form.Select>
                )}
            </Form.Group>

            {/* --- Branding --------------------------------------------- */}
            <h2 className="h6 text-muted mt-4 mb-2">Branding</h2>
            <Form.Group className="mb-3" controlId="ecosystem-logoUrl">
                <Form.Label>Logo URL</Form.Label>
                <Form.Control
                    type="text"
                    value={form.logoUrl}
                    placeholder="https://…"
                    onChange={(e) => setField('logoUrl', e.target.value)}
                />
            </Form.Group>
            <Row>
                <Col md={6}>
                    <Form.Group
                        className="mb-3"
                        controlId="ecosystem-primaryColor"
                    >
                        <Form.Label>Primary colour</Form.Label>
                        <Form.Control
                            type="text"
                            className="platform-admin-hex"
                            value={form.primaryColor}
                            placeholder="#C8102E"
                            isInvalid={Boolean(errors.primaryColor)}
                            onChange={(e) =>
                                setField('primaryColor', e.target.value)
                            }
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.primaryColor}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group
                        className="mb-3"
                        controlId="ecosystem-secondaryColor"
                    >
                        <Form.Label>Secondary colour</Form.Label>
                        <Form.Control
                            type="text"
                            className="platform-admin-hex"
                            value={form.secondaryColor}
                            placeholder="#0B1F3A"
                            isInvalid={Boolean(errors.secondaryColor)}
                            onChange={(e) =>
                                setField('secondaryColor', e.target.value)
                            }
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.secondaryColor}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>
            <Form.Group className="mb-3" controlId="ecosystem-faviconUrl">
                <Form.Label>Favicon URL</Form.Label>
                <Form.Control
                    type="text"
                    value={form.faviconUrl}
                    placeholder="https://…"
                    onChange={(e) => setField('faviconUrl', e.target.value)}
                />
            </Form.Group>

            {/* --- Capabilities ----------------------------------------- */}
            <h2 className="h6 text-muted mt-4 mb-2">Capabilities</h2>
            <p className="small text-muted">
                Feature flags that gate which features this ecosystem exposes.
            </p>
            <Row>
                {CAPABILITY_FIELDS.map(({ key, label }) => (
                    <Col sm={6} key={key}>
                        <Form.Check
                            type="switch"
                            id={`capability-${key}`}
                            label={label}
                            className="mb-2"
                            checked={form.capabilities[key]}
                            onChange={(e) =>
                                setCapability(key, e.target.checked)
                            }
                        />
                    </Col>
                ))}
            </Row>

            {/* --- Taxonomy --------------------------------------------- */}
            <h2 className="h6 text-muted mt-4 mb-2">Taxonomy</h2>
            <p className="small text-muted">
                One value per line (commas also accepted). Replaces the
                hardcoded Colorado-specific lists for this ecosystem.
            </p>
            {TAXONOMY_FIELDS.map(({ key, label, help }) => (
                <Form.Group
                    className="mb-3"
                    controlId={`ecosystem-${key}`}
                    key={key}
                >
                    <Form.Label>{label}</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={form[key]}
                        onChange={(e) => setField(key, e.target.value)}
                    />
                    <Form.Text className="text-muted">{help}</Form.Text>
                </Form.Group>
            ))}

            {/* --- Actions ---------------------------------------------- */}
            <div className="d-flex justify-content-end gap-2 mt-4">
                <Button
                    variant="secondary"
                    type="button"
                    onClick={onCancel}
                    disabled={submitting}
                >
                    Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={submitting}>
                    {submitting ? (
                        <>
                            <Spinner
                                animation="border"
                                size="sm"
                                className="me-2"
                            />
                            Saving…
                        </>
                    ) : isEdit ? (
                        'Save Changes'
                    ) : (
                        'Create Ecosystem'
                    )}
                </Button>
            </div>
        </Form>
    );
}

EcosystemForm.propTypes = {
    // 'create' for a new ecosystem, 'edit' for an existing one.
    mode: PropTypes.oneOf(['create', 'edit']).isRequired,
    // The ecosystem being edited (edit mode); null / omitted on create.
    ecosystem: PropTypes.object,
    // True while the caller's create / update request is in flight.
    submitting: PropTypes.bool,
    // A server-side error message to surface inline (e.g. a slug 400).
    serverError: PropTypes.string,
    // Receives the assembled API payload when the form passes validation.
    onSubmit: PropTypes.func.isRequired,
    // Invoked when the admin cancels.
    onCancel: PropTypes.func.isRequired,
};

EcosystemForm.defaultProps = {
    ecosystem: null,
    submitting: false,
    serverError: '',
};
